import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import FormField from '@/components/common/FormField'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { showToastNotification } from '@/utility/helper'
import { ThrowError } from '@/api/callApi'
import EmployeeOnBoardService from '@/services/EmployeeOnBoard/EmployeeOnBoardService'
import OffboardingService from '@/services/Offboarding/OffboardingService'
import type { IUser } from '@/interface/employeeOnBoard'
import type { IOffboardingDetails } from '@/interface/offboarding.interface'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import SearchSelect from '@/components/common/SearchSelect'

interface ExitDetailsStepProps {
    isLoading: boolean;
    setIsLoading: (val: boolean) => void;
    userId: string;
    onNext: () => void;
    isCompleted: boolean;
    initialData?: (IOffboardingDetails & { handoverToUser?: { id: string; name: string; designation?: any } }) | null;
    id: string;
}

const ExitDetailsStep: React.FC<ExitDetailsStepProps> = ({ isLoading, setIsLoading, userId, onNext, isCompleted, initialData, id }) => {
    const [allEmployees, setAllEmployees] = useState<IUser[]>([])
    const [isHandoverSearching, setIsHandoverSearching] = useState(false)
    const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<IOffboardingDetails>({
        defaultValues: {
            lastWorkingDate: "",
            resignationDate: "",
            reason: "Resignation",
            noticePeriod: "",
            relievingDate: "",
            remarks: "",
            handoverToUserId: ""
        }
    })
    const { siteId } = useCurrentUser();

    useEffect(() => {
        register("handoverToUserId", { required: "Handover employee is required" })
    }, [register])

    // Load dropdown handover employees list with server-side search
    const fetchHandoverList = useCallback(async (search = "") => {
        setIsHandoverSearching(true)
        try {
            const siteParam = siteId ? `&siteId=${siteId}` : ""
            const searchParam = search ? `&search=${encodeURIComponent(search)}` : ""
            const res = await EmployeeOnBoardService.getAllEmployees(
                `isVerified=1&status=current&pageSize=100${siteParam}${searchParam}`
            )
            if (res?.status && res?.data?.user) {
                setAllEmployees(res.data.user)
            }
        } catch (err) {
            console.error("Error loading handover list:", err)
        } finally {
            setIsHandoverSearching(false)
        }
    }, [siteId])

    useEffect(() => {
        fetchHandoverList()
    }, [fetchHandoverList])

    // Populate form with initialData whenever it changes
    useEffect(() => {
        if (initialData) {
            const handoverId = initialData.handoverToUserId || initialData.handoverToUser?.id || ""
            reset({
                lastWorkingDate: initialData.lastWorkingDate || "",
                resignationDate: initialData.resignationDate || "",
                reason: initialData.reason || "Resignation",
                noticePeriod: initialData.noticePeriod || "",
                relievingDate: initialData.relievingDate || "",
                remarks: initialData.remarks || "",
                handoverToUserId: handoverId
            })
            if (handoverId) {
                setValue("handoverToUserId", handoverId, { shouldValidate: true })
            }
        }
    }, [initialData, reset, setValue])

    const onSubmit = async (data: IOffboardingDetails) => {
        if (!userId) return
        const payload = {
            handoverToUserId: data.handoverToUserId,
            lastWorkingDate: data.lastWorkingDate,
            resignationDate: data.resignationDate,
            reason: data.reason,
            noticePeriod: data.noticePeriod,
            relievingDate: data.relievingDate,
            remarks: data.remarks,
            status: "Initiated"
        }
        setIsLoading(true)
        try {
            const res = id
                ? await OffboardingService.updateOffboarding(id, payload)
                : await OffboardingService.initiateOffboarding(userId, payload)
            if (res?.status) {
                showToastNotification("success", "Offboarding process details saved successfully")
                onNext()
            }
        } catch (err) {
            ThrowError(err)
        } finally {
            setIsLoading(false)
        }
    }

    const currentHandoverId = watch("handoverToUserId");
    const resignationDate = watch("resignationDate");
    const noticePeriod = watch("noticePeriod");

    const todayStr = new Date().toISOString().split("T")[0];

    // Minimum allowed Last Working Date & Relieving Date = Resignation Date + Notice Period (days)
    const minExitDate = useMemo(() => {
        if (!resignationDate) return undefined;
        const resDate = new Date(resignationDate);
        if (isNaN(resDate.getTime())) return undefined;

        const days = parseInt(String(noticePeriod || "0").replace(/[^0-9]/g, ""), 10) || 0;
        const minD = new Date(resDate);
        minD.setDate(minD.getDate() + days);
        return minD.toISOString().split("T")[0];
    }, [resignationDate, noticePeriod]);

    // Filter out users whose designation is level 4/5 (Staff/Guest) & preserve currently selected user
    const handoverOptions = useMemo(() => {
        const list = [...allEmployees]

        // Ensure initial handover employee is present in the options list even if not in current page
        if (initialData?.handoverToUser && !list.some((e) => e.id === initialData.handoverToUser?.id)) {
            list.unshift({
                id: initialData.handoverToUser.id,
                name: initialData.handoverToUser.name,
                designation: initialData.handoverToUser.designation,
            } as any)
        }

        return list
            .filter((emp) => {
                if (!emp?.id) return false
                const level = emp.designation?.level !== undefined ? Number(emp.designation.level) : null
                // If this is the currently selected handover user, preserve it so it remains visible
                if (currentHandoverId && emp.id === currentHandoverId) return true
                if (level === 4 || level === 5) return false
                return true
            })
            .map((emp) => ({
                value: emp.id,
                label: `${emp.name} (${emp.designation?.name || "N/A"})`
            }))
    }, [allEmployees, currentHandoverId, initialData])

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                    type="date"
                    label="Resignation Date"
                    disabled={isCompleted}
                    max={todayStr}
                    {...register("resignationDate", {
                        required: "Resignation Date is required",
                        validate: (val) => {
                            if (!val) return true;
                            if (val > todayStr) {
                                return "Resignation Date cannot be in the future";
                            }
                            return true;
                        },
                    })}
                    error={errors.resignationDate?.message}
                    required
                />
                <FormField
                    type="text"
                    label="Notice Period (in days, e.g. 30)"
                    placeholder="Enter notice period (e.g. 30)"
                    disabled={isCompleted}
                    maxLength={3}
                    {...register("noticePeriod", {
                        required: "Notice period is required",
                        pattern: {
                            value: /^[0-9]+$/,
                            message: "Notice period must be a valid number of days",
                        },
                    })}
                    error={errors.noticePeriod?.message}
                    required
                />
                <FormField
                    type="date"
                    label="Last Working Day"
                    disabled={isCompleted}
                    min={minExitDate || resignationDate}
                    {...register("lastWorkingDate", {
                        required: "Last Working Day is required",
                        validate: (val) => {
                            if (!val) return true;
                            if (minExitDate && val < minExitDate) {
                                return `Last Working Day must be on or after ${minExitDate} (Resignation Date + Notice Period)`;
                            }
                            if (resignationDate && val < resignationDate) {
                                return "Last Working Day cannot be before Resignation Date";
                            }
                            return true;
                        },
                    })}
                    error={errors.lastWorkingDate?.message}
                    required
                />
                <FormField
                    type="date"
                    label="Relieving Date"
                    disabled={isCompleted}
                    min={minExitDate || resignationDate}
                    {...register("relievingDate", {
                        required: "Relieving Date is required",
                        validate: (val) => {
                            if (!val) return true;
                            if (minExitDate && val < minExitDate) {
                                return `Relieving Date must be on or after ${minExitDate} (Resignation Date + Notice Period)`;
                            }
                            if (resignationDate && val < resignationDate) {
                                return "Relieving Date cannot be before Resignation Date";
                            }
                            return true;
                        },
                    })}
                    error={errors.relievingDate?.message}
                    required
                />
                <FormField
                    type="select"
                    label="Reason for exit"
                    disabled={isCompleted}
                    {...register("reason")}
                    value={watch("reason")}
                    options={[
                        { value: "Resignation", label: "Resignation" },
                        { value: "Termination", label: "Termination" },
                        { value: "Retirement", label: "Retirement" },
                        { value: "Other", label: "Other / Personal Reasons" }
                    ]}
                    required
                />
                <SearchSelect
                    label="Handover Work To"
                    disabled={isCompleted}
                    required
                    placeholder="Select Handover User"
                    options={handoverOptions}
                    value={currentHandoverId || ""}
                    onChange={(val) => setValue("handoverToUserId", val, { shouldValidate: true, shouldDirty: true })}
                    onSearch={fetchHandoverList}
                    isLoading={isHandoverSearching}
                    error={errors.handoverToUserId?.message}
                />
            </div>
            <FormField
                type="textarea"
                label="Remarks / Comments"
                placeholder="Add notice details, notes, etc."
                disabled={isCompleted}
                rows={4}
                {...register("remarks")}
                required
            />
            <div className="flex justify-end pt-4">
                {!isCompleted && (
                    <Button type="submit" disabled={isLoading} className="px-6 rounded-xl">
                        {isLoading ? <Spinner /> : 'Save Exit Details'}
                    </Button>
                )}
            </div>
        </form>
    )
}

export default ExitDetailsStep
